/*
  Lion's Mane Tea Box "pick 4" chooser (snippets/cb-tea-box-chooser.liquid) in the product buy box
  (sections/cb-pdp-main.liquid).

  - One row per tea ([data-cb-teabox-item]: data-code is the tea's SKU letter, data-match its name in the variant
    option values). Minus and plus change the tea's count; plus is off once the box is full (data-total, 4), minus at 0.
    The buttons use aria-disabled, so they keep focus; pressing one that is off reads the count again.
  - Each variant says what it holds: its option value "2 English Breakfast, 1 Green, 1 Mint & Chamomile", or else
    its SKU "LMTEA4-B2G1L0M1". When the counts make the total, the matching variant is picked by checking the
    section's own option radio (hidden under the chooser) and firing change on it, so the section's script does what
    it does for any size: the hidden id input, the price, ?variant= in the URL and cb:variant-change for the
    purchase options.
  - Until then the Add to basket button stays off with "Choose [left] more": the chooser's data-cb-buy-hold, which the
    section's script reads (cb:buy-hold asks it to look again). A product with no plans yet keeps its "Coming soon"
    hold (data-soon) whatever the counts.
  - The count line is a live region (role="status"): after each change it reads "[tea]: [count]" and the count.
  - Where it starts (James, 16 Oct 2026, RULES 135: never preselect a mix). The box opens EMPTY, 0 of 4, with the
    button held on "Choose 4 more", as the Liquid already paints it (no flash of a full box). ?tea=<SKU letter> (the
    single teas' "Build your Tea Box" link) starts on one of that tea ("Choose 3 more"). ?variant= wins over both
    and opens on that mix, but only when the Liquid opened on it too (data-open: blank for no ?variant= and for a
    sold-out one, which the section swaps for the first available mix, a mix nobody chose).
  - While the box is held (fewer than 4, or Coming soon) the form can't be sent by any route: the buttons are off
    (the section's script), and a submit listener on the form (Enter in a field, the sticky bar, any app button,
    requestSubmit) stops it before assets/cb-cart.js sees it, so the variant still in the hidden id input is never
    added.
  A tea whose every mix is sold out shows Sold out and its plus is off.
  - The comparison by the price ([data-cb-teabox-vs], "[saving] less than four single boxes") is worked out again for
    each mix from the teas' own prices (data-price) and the plan price (last price adjustment).
  window.CbTeaBox exposes the mapping functions for tools/cb-render/test/tea-box.test.mjs.
*/
(function () {
  'use strict';

  function norm(text) {
    return String(text == null ? '' : text)
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function zeros(n) {
    var out = [];
    for (var i = 0; i < n; i++) out.push(0);
    return out;
  }

  /* Counts per tea (in the order of items) from an option value such as
     "2 English Breakfast, 1 Green, 1 Mint & Chamomile"; null when any part names no tea. */
  function countsFromTitle(title, items) {
    var text = String(title == null ? '' : title).trim();
    if (!text) return null;
    var counts = zeros(items.length);
    var parts = text.split(',');
    for (var i = 0; i < parts.length; i++) {
      var m = parts[i].trim().match(/^(\d+)\s*(?:x\s+)?(.+)$/i);
      if (!m) return null;
      var at = -1;
      for (var j = 0; j < items.length; j++) {
        if (norm(items[j].match || items[j].name) === norm(m[2])) {
          at = j;
          break;
        }
      }
      if (at < 0) return null;
      counts[at] += parseInt(m[1], 10);
    }
    return counts;
  }

  /* Counts per tea from a SKU ending in letter and count pairs ("LMTEA4-B2G1L0M1"); null when it has none, when a
     tea has no letter or when it counts a letter no tea has. */
  function countsFromSku(sku, items) {
    var tail = String(sku == null ? '' : sku).split('-').pop().trim().toUpperCase();
    if (!/^([A-Z]\d+)+$/.test(tail)) return null;
    var found = {};
    tail.replace(/([A-Z])(\d+)/g, function (all, letter, n) {
      found[letter] = (found[letter] || 0) + parseInt(n, 10);
      return all;
    });
    var counts = [];
    for (var i = 0; i < items.length; i++) {
      var code = String(items[i].code || '').trim().toUpperCase();
      if (!code) return null;
      counts.push(found[code] || 0);
      delete found[code];
    }
    for (var left in found) {
      if (Object.prototype.hasOwnProperty.call(found, left) && found[left] > 0) return null;
    }
    return counts;
  }

  /* What a variant holds: its option value first, else its SKU. */
  function countsOf(variant, items) {
    if (!variant) return null;
    var value = variant.option1 != null ? variant.option1 : variant.options && variant.options[0];
    return countsFromTitle(value != null ? value : variant.title, items) || countsFromSku(variant.sku, items);
  }

  function keyOf(counts) {
    return counts.join('-');
  }

  /* counts key -> variant, for every variant that says what it holds (the first one wins). */
  function indexVariants(variants, items) {
    var map = {};
    (variants || []).forEach(function (variant) {
      var counts = countsOf(variant, items);
      if (counts && !map[keyOf(counts)]) map[keyOf(counts)] = variant;
    });
    return map;
  }

  /* The variant holding exactly these counts, or null. */
  function findMix(index, counts) {
    return index[keyOf(counts)] || null;
  }

  /* The counts the chooser starts on (RULES 135: the box opens empty). search is location.search; open is the
     chooser's data-open (the variant id the Liquid opened on, '' when it opened empty, null when not printed);
     soldOut[i] is true when no mix with tea i can be bought. */
  function startCounts(search, open, variants, items, soldOut) {
    var params;
    try {
      params = new URLSearchParams(search || '');
    } catch (err) {
      return zeros(items.length);
    }
    var asked = String(params.get('variant') || '').trim();
    if (asked) {
      var id = open == null ? asked : String(open).trim();
      for (var v = 0; v < (variants || []).length; v++) {
        var variant = variants[v];
        if (!id || String(variant.id) !== id) continue;
        if (open == null && !variant.available) break;
        var mix = countsOf(variant, items);
        if (mix) return mix;
      }
      return zeros(items.length);
    }
    var tea = norm(params.get('tea'));
    var counts = zeros(items.length);
    for (var i = 0; tea && i < items.length; i++) {
      if (norm(items[i].code) === tea && !(soldOut && soldOut[i])) {
        counts[i] = 1;
        break;
      }
    }
    return counts;
  }

  function fill(text, values) {
    return String(text || '').replace(/\[(\w+)\]/g, function (all, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : all;
    });
  }

  function init(el) {
    if (!el || el.hasAttribute('data-cb-teabox-ready')) return;
    var root = el.closest('[data-cb-pdp]');
    var dataEl = root && root.querySelector('[data-cb-pdp-json]');
    if (!dataEl) return;
    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (err) {
      return;
    }
    el.setAttribute('data-cb-teabox-ready', '');

    var rows = Array.prototype.slice.call(el.querySelectorAll('[data-cb-teabox-item]'));
    var items = rows.map(function (row) {
      return {
        row: row,
        code: row.getAttribute('data-code') || '',
        match: row.getAttribute('data-match') || row.getAttribute('data-name') || '',
        name: row.getAttribute('data-name') || '',
        count: row.querySelector('[data-cb-teabox-count]'),
        minus: row.querySelector('[data-cb-teabox-step="-1"]'),
        plus: row.querySelector('[data-cb-teabox-step="1"]'),
        sold: row.querySelector('[data-cb-teabox-sold]'),
        badge: (parseInt(row.getAttribute('data-badge'), 10) || 0) > 0,
        price: row.getAttribute('data-price') === '' ? null : parseInt(row.getAttribute('data-price'), 10),
      };
    });
    if (!items.length) return;
    var total = parseInt(el.getAttribute('data-total'), 10) || 4;
    var variants = data.variants || [];
    var index = indexVariants(variants, items);
    var radios = Array.prototype.slice.call(root.querySelectorAll('[data-cb-option] input[type="radio"]'));
    var idInput = root.querySelector('[data-cb-variant-input]');
    var statusText = el.querySelector('[data-cb-teabox-text]');
    var said = el.querySelector('[data-cb-teabox-said]');
    var soon = el.getAttribute('data-soon') || '';
    var form = root.querySelector('form[data-cb-add-form]');
    /* "[saving] less than four single boxes" by the price (part 'per'): kept true for each mix. */
    var vs = root.querySelector('[data-cb-teabox-vs]');

    /* The variant's plan price: the plan the form posts (else its first plan), at its last price adjustment. */
    function planPrice(variant) {
      var allocations = (variant && variant.selling_plan_allocations) || [];
      if (!allocations.length) return null;
      var chosen = '';
      try {
        chosen = form ? String(new FormData(form).get('selling_plan') || '') : '';
      } catch (err) {
        chosen = '';
      }
      var allocation = allocations[0];
      for (var a = 0; a < allocations.length; a++) {
        if (String(allocations[a].selling_plan_id) === chosen) allocation = allocations[a];
      }
      var steps = allocation.price_adjustments || [];
      return steps.length ? steps[steps.length - 1].price : allocation.price;
    }

    function compare(variant) {
      if (!vs || !variant) return;
      var single = 0;
      for (var i = 0; i < items.length; i++) {
        if (!counts[i]) continue;
        if (items[i].price == null || isNaN(items[i].price)) {
          vs.hidden = true;
          return;
        }
        single += items[i].price * counts[i];
      }
      var sub = planPrice(variant);
      var save = sub != null && single > sub ? Math.floor(((single - sub) * 100) / single) : 0;
      vs.hidden = save < 1;
      if (save >= 1) vs.textContent = fill(vs.getAttribute('data-template'), { saving: save + '%' });
    }

    /* A tea is sold out when no mix with it can be bought. */
    var soldOut = items.map(function (item, i) {
      return !variants.some(function (variant) {
        var counts = countsOf(variant, items);
        return variant.available && counts && counts[i] > 0;
      });
    });

    function sum(counts) {
      return counts.reduce(function (a, b) {
        return a + b;
      }, 0);
    }

    /* Where to start (RULES 135): empty; one of the ?tea= tea when the URL names no variant; the mix of a ?variant=
       the Liquid opened on (data-open; an older render without it: the URL's variant when it can be bought). Never
       the variant in the hidden id input, which is the first mix whatever the shopper wants. */
    var counts = startCounts(window.location.search, el.getAttribute('data-open'), variants, items, soldOut);

    function setOff(button, off) {
      if (!button) return;
      if (off) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    }

    function status() {
      var chosen = sum(counts);
      var text = fill(el.getAttribute('data-status'), { chosen: chosen, total: total });
      if (chosen < total) text += ' · ' + fill(el.getAttribute('data-more'), { left: total - chosen });
      return text;
    }

    function radioFor(variant) {
      var value = variant.option1 != null ? variant.option1 : variant.options && variant.options[0];
      for (var r = 0; r < radios.length; r++) {
        if (radios[r].value === value) return radios[r];
      }
      return null;
    }

    /* The section's own option radio for this variant: checked, then change, as a shopper's size choice. On the
       first render only when it is not the one the page opened on (no ?variant= added to the URL on load). */
    function pick(variant, quiet) {
      var radio = radioFor(variant);
      if (!radio) {
        /* No radio (unexpected): keep the form's id in step at least. */
        if (idInput) idInput.value = variant.id;
        return;
      }
      if (quiet && radio.checked && idInput && String(idInput.value) === String(variant.id)) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function render(changed, quiet) {
      var chosen = sum(counts);
      var left = total - chosen;
      items.forEach(function (item, i) {
        if (item.count) item.count.textContent = String(counts[i]);
        item.row.classList.toggle('is-on', counts[i] > 0);
        item.row.classList.toggle('is-sold-out', soldOut[i]);
        if (item.sold) item.sold.hidden = !soldOut[i];
        item.row.classList.toggle('is-plain', !item.badge && !soldOut[i]);
        setOff(item.minus, counts[i] <= 0);
        setOff(item.plus, left <= 0 || soldOut[i]);
      });
      el.classList.toggle('is-full', left === 0);
      if (statusText) statusText.textContent = status();

      var variant = left === 0 ? findMix(index, counts) : null;
      var hold = soon;
      if (!hold && left > 0) hold = fill(el.getAttribute('data-more'), { left: left });
      if (!hold && left < 0) hold = fill(el.getAttribute('data-more'), { left: 0 });
      if (!hold && !variant) hold = el.getAttribute('data-unavailable') || '';
      el.setAttribute('data-cb-buy-hold', hold);
      if (variant) pick(variant, quiet);
      if (variant) compare(variant);
      el.dispatchEvent(new CustomEvent('cb:buy-hold', { bubbles: true, detail: { variant: variant, counts: counts.slice() } }));

      if (quiet) return;
      if (said) {
        var item = changed != null ? items[changed] : null;
        said.textContent = item ? fill(el.getAttribute('data-count-label'), { tea: item.name, count: counts[changed] }) + '. ' : '';
      }
    }

    el.addEventListener('click', function (event) {
      var button = event.target instanceof Element ? event.target.closest('[data-cb-teabox-step]') : null;
      if (!button || !el.contains(button)) return;
      var row = button.closest('[data-cb-teabox-item]');
      var i = rows.indexOf(row);
      if (i < 0) return;
      if (button.getAttribute('aria-disabled') === 'true') {
        /* Off: say the count again, so a screen reader user knows why nothing changed. */
        if (said) said.textContent = '';
        if (statusText) {
          statusText.textContent = '';
          window.setTimeout(function () {
            statusText.textContent = status();
          }, 50);
        }
        return;
      }
      var step = parseInt(button.getAttribute('data-cb-teabox-step'), 10) || 0;
      var next = counts[i] + step;
      if (next < 0 || (step > 0 && sum(counts) >= total)) return;
      counts[i] = next;
      render(i, false);
    });

    if (form) {
      form.addEventListener('cb:plan-change', function () {
        if (sum(counts) === total) compare(findMix(index, counts));
      });
      /* Held (fewer than 4 chosen, Coming soon, a mix with no variant): no route may send the form, or the variant
         still in the hidden id input (the first mix) would be added. Capture on the form runs before
         assets/cb-cart.js (submit on document), which leaves a prevented submit alone. */
      form.addEventListener('submit', function (event) {
        if (!el.isConnected || !el.getAttribute('data-cb-buy-hold')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (statusText) {
          statusText.textContent = '';
          window.setTimeout(function () {
            statusText.textContent = status();
          }, 50);
        }
      }, true);
    }

    render(null, true);
  }

  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-cb-teabox]'), init);
  }

  window.CbTeaBox = {
    countsFromTitle: countsFromTitle,
    countsFromSku: countsFromSku,
    countsOf: countsOf,
    indexVariants: indexVariants,
    findMix: findMix,
    startCounts: startCounts,
    init: init,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
